import { User, WebStorageStateStore } from "oidc-client-ts";
import { Store } from "./store";

export const loadConfig = () => {
  return fetch("env.cfg")
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .then((config) => {
      // Store all properties of the config object in local storage
      Object.keys(config).forEach((key) => {
        sessionStorage.setItem(key, config[key]);
      });

      sessionStorage.setItem("isConfigLoaded", "true");
      return config;
    })
    .catch((error) => {
      console.error("Failed to load config:", error);
      sessionStorage.setItem("isConfigLoaded", "false");
      return null;
    });
};

export const getOidcKey = () => {
  if (sessionStorage.getItem("isConfigLoaded") !== "true") {
    console.warn("Configuration not loaded. Loading now...");
    loadConfig();
  }

  return `oidc.user:${sessionStorage.getItem("AUTHORITY")}:${sessionStorage.getItem("CLIENT_ID")}`;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const onSigninCallback = (_user: User | void): void => {
  const params = new URLSearchParams(window.location.search);
  const popup = params.get("Popup");
  const nextSearch = popup ? `?Popup=${encodeURIComponent(popup)}` : "";
  window.history.replaceState({}, document.title, `${window.location.pathname}${nextSearch}${window.location.hash}`);
};

export const getIdpConfig = () => {
  if (sessionStorage.getItem("isConfigLoaded") !== "true") {
    console.warn("Configuration not loaded. Loading now...");
    loadConfig();
  }

  const AUTHORITY = sessionStorage.getItem("AUTHORITY") || process.env.AUTHORITY;
  const CLIENT_ID = sessionStorage.getItem("CLIENT_ID") || process.env.CLIENT_ID;
  const CLIENT_SECRET = sessionStorage.getItem("CLIENT_SECRET") || process.env.CLIENT_SECRET;
  const CLIENT_ROOT = sessionStorage.getItem("CLIENT_ROOT") || process.env.CLIENT_ROOT;
  const CLIENT_SCOPE = sessionStorage.getItem("CLIENT_SCOPE") || process.env.CLIENT_SCOPE;
  const AUDIENCE = sessionStorage.getItem("AUDIENCE") || process.env.AUDIENCE;

  return {
    authority: AUTHORITY,
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
    redirect_uri: `${CLIENT_ROOT}/taskpane.html`,
    silent_redirect_uri: `${CLIENT_ROOT}/taskpane.html`,
    onSigninCallback,
    post_logout_redirect_uri: `${CLIENT_ROOT}/taskpane.html`,
    response_type: "code",
    scope: CLIENT_SCOPE,
    userStore: new WebStorageStateStore({ store: Store }),
    //prompt: "login",
    useRefreshTokens: true,
    cacheLocation: "localstorage",
    extraQueryParams: {
      audience: AUDIENCE,
    },
    metadata: {
      issuer: AUTHORITY,
      end_session_endpoint: `${AUTHORITY}/v2/logout?client_id=${CLIENT_ID}&returnTo=${CLIENT_ROOT}/taskpane.html`,
      authorization_endpoint: `${AUTHORITY}/authorize`,
      token_endpoint: `${AUTHORITY}/oauth/token`,
      device_authorization_endpoint: `${AUTHORITY}/oauth/device/code`,
      userinfo_endpoint: `${AUTHORITY}/userinfo`,
      mfa_challenge_endpoint: `${AUTHORITY}/mfa/challenge`,
      jwks_uri: `${AUTHORITY}/.well-known/jwks.json`,
      registration_endpoint: `${AUTHORITY}/oidc/register`,
      revocation_endpoint: `${AUTHORITY}/oauth/revoke`,
      scopes_supported: [
        "openid",
        "profile",
        "offline_access",
        "name",
        "given_name",
        "family_name",
        "nickname",
        "email",
        "email_verified",
        "picture",
        "created_at",
        "identities",
        "phone",
        "address",
      ],
      response_types_supported: [
        "code",
        "token",
        "id_token",
        "code token",
        "code id_token",
        "token id_token",
        "code token id_token",
      ],
      code_challenge_methods_supported: ["S256", "plain"],
      response_modes_supported: ["query", "fragment", "form_post"],
      subject_types_supported: ["public"],
      id_token_signing_alg_values_supported: ["HS256", "RS256", "PS256"],
      token_endpoint_auth_methods_supported: ["client_secret_basic", "client_secret_post", "private_key_jwt"],
      claims_supported: [
        "aud",
        "auth_time",
        "created_at",
        "email",
        "email_verified",
        "exp",
        "family_name",
        "given_name",
        "iat",
        "identities",
        "iss",
        "name",
        "nickname",
        "phone_number",
        "picture",
        "sub",
      ],
      request_uri_parameter_supported: false,
      request_parameter_supported: false,
      token_endpoint_auth_signing_alg_values_supported: ["RS256", "RS384", "PS256"],
      backchannel_logout_supported: true,
      backchannel_logout_session_supported: true,
    },
  };
};
